import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { GetContainerDetailsInput, GetContainerDetailsOutput } from 'src/models/GetContainerDetails';
import { resetUiAlertState, setUiAlertState, setIsLoading, resetIsLoading } from 'src/store/common-slices/uiSlice';
import { AlertLevels } from 'src/models/alert-model';
import * as ErrorConstants from 'src/localization/string-ids/error-constants';
import RIVBFFService from 'src/services/riv-bff-service';
import {RIVApiMethods} from "src/constants/api-method-constants";

interface getContainerState {
    containerDetailsOutput?: GetContainerDetailsOutput;
}

const initialState: getContainerState = {
    containerDetailsOutput: undefined,
}

export const fetchContainerDetails = createAsyncThunk<GetContainerDetailsOutput, GetContainerDetailsInput>(
    'getContainer/fetchContainerDetails',
    async (getContainerDetailsInput: GetContainerDetailsInput, { dispatch, rejectWithValue }) => {
        dispatch(resetUiAlertState());
        dispatch(setIsLoading());

        try {
            const getContainerDetailsOutput = await RIVBFFService.triggerGetContainerDetailsAPI(getContainerDetailsInput);
            dispatch(resetIsLoading());
            return getContainerDetailsOutput;

        } catch (error) {
            dispatch(resetIsLoading());
            if (error && typeof error === 'object' && 'errorMessage' in error && 'errorStatus' in error) {
                const apiError = error as { errorMessage: string; errorStatus: number };
                
                dispatch(setUiAlertState({
                    alertLevel: AlertLevels.ERROR,
                    alertTitleString: `${getContainerDetailsInput.containerId}`,
                    alertMessageString: apiError.errorMessage,
                    alertStatus: apiError.errorStatus,
                    UIAction: RIVApiMethods.GET_CONTAINER,
                    warehouse: getContainerDetailsInput.warehouseId,
                    associate: getContainerDetailsInput.associate,
                    mode: getContainerDetailsInput.mode
                }));
                return rejectWithValue({
                    data: apiError.errorMessage,
                    status: apiError.errorStatus
                });
            }
            else if (error instanceof Error) {
                dispatch(setUiAlertState({
                    alertLevel: AlertLevels.ERROR,
                    alertTitleString: `${getContainerDetailsInput.containerId}`,
                    alertMessageString: error.message,
                }));
                return rejectWithValue(error.message);
            }
            else {
                dispatch(setUiAlertState({
                    alertLevel: AlertLevels.ERROR,
                    alertTitleString: `${getContainerDetailsInput.containerId}`,
                    alertMessageString: ErrorConstants.UNKNOWN_ERROR,
                }));
                return rejectWithValue(ErrorConstants.UNKNOWN_ERROR);
            }

        }
    }
);

const getContainerSlice = createSlice({
    name: 'getContainer',
    initialState,
    reducers: {
        resetContainerState: (state) => {
            state.containerDetailsOutput = initialState.containerDetailsOutput;
        }
    },
    extraReducers: builder => {
        builder
            .addCase(fetchContainerDetails.pending, (state) => {
                state.containerDetailsOutput = undefined;
            })
            .addCase(fetchContainerDetails.fulfilled, (state, action) => {
                state.containerDetailsOutput = action.payload;
            })
            .addCase(fetchContainerDetails.rejected, (state, action) => {
                state.containerDetailsOutput = undefined;
            })
    }
});

export const { resetContainerState } = getContainerSlice.actions;
export default getContainerSlice.reducer;
